import React, { useState } from 'react';
import { Box, Typography, Drawer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Tooltip, Button } from '@mui/material';
import PropTypes from 'prop-types';
import { useDataProvider, useNotify, useRefresh, useTranslate } from 'react-admin';
import { useImportErrorHandler } from '@/composables/useImportErrorHandler';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import ActiveIcon from '@/assets/icons/active-icon.svg?react';
import WarningIcon from '@/assets/icons/warning-icon.svg?react';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import UploadIcon from '@/assets/icons/upload-icon.svg?react';
import { Palette } from '@/themes/themeOverrides/Palette';
import { pluralizeWord } from '@/helpers/format/pluralizeWord';
import { styled } from '@mui/material/styles';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';

const ImportValidationDetailsDrawer = ({ open, onClose, data, resource, dataTransformer, setLoading, loading, onImportSuccess }) => {
    const translate = useTranslate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedAddresses, setSelectedAddresses] = useState({});

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const errorHandler = useImportErrorHandler();

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const StyledBox = styled(Box)({
        '&.validation-summary': {
            '& .validation-summary-title': {
                fontSize: '16px',
                fontWeight: '600',
                lineHeight: '22px',
                letterSpacing: '-0.16px',
                marginBottom: '20px',
            },
            '& .validation-summary-details': {
                position: 'relative',
                '& .MuiTypography-body2': {
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '-0.14px',
                    padding: '15px 0',
                    display: 'flex',
                    alignItems: 'center',
                    borderTop: `2px solid ${Palette.gray.gray75}`,
                    borderBottom: `2px solid ${Palette.gray.gray75}`,
                    '& svg': {
                        width: '24px',
                        height: '24px',
                        marginRight: '16px',
                    },
                    '& + .MuiTypography-body2': {
                        borderTop: 'none',
                    },
                    '& .MuiTypography-body1': {
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '-0.14px',
                    },
                },
                '& .MuiButton-root': {
                    position: 'absolute',
                    right: '0',
                    bottom: '21px',
                    fontSize: '11px',
                    lineHeight: '16px !important',
                    padding: '8px 16px 8px 12px !important',
                    borderRadius: '10px',
                    [`@media (max-width: ${breakpoints.values.md}px)`]: {
                        position: 'relative',
                        bottom: 'auto',
                        top: '10px',
                    },
                    '& svg': {
                        width: '16px',
                        height: '16px',
                    },
                },
            },
        },
    });

    const submitValidData = async () => {
        if (!data) {
            console.error('Brak danych walidacji do importu');
            return;
        }

        const list = Array.isArray(data.list) ? data.list : [];

        const devices = (() => {
            const acc = [];
            for (let originalIndex = 0; originalIndex < list.length; originalIndex++) {
                const item = list[originalIndex];
                const errors = Array.isArray(item?.errorList) ? item.errorList : [];
                const dev = item?.device;
                if (!dev) continue;
                const sel = selectedAddresses ? selectedAddresses[originalIndex] : undefined;
                const effectiveErrors = sel ? errors.filter(e => e.field !== 'formattedAddress' && e.field !== 'placeId') : errors;
                if (effectiveErrors.length > 0) continue;
                if (sel) {
                    acc.push({
                        ...dev,
                        placeId: sel.placeId ?? dev.placeId,
                        formattedAddress: sel.formattedAddress ?? dev.formattedAddress,
                    });
                } else {
                    acc.push(dev);
                }
            }
            return acc.filter(Boolean);
        })();

        if (devices.length === 0) {
            notify(translate('common.importSection.noValidRecords'), { type: 'warning' });
            return;
        }

        try {
            setLoading(true);
            await dataProvider
                .post(`${resource}/import`, { data: { devices, dataTransformerName: dataTransformer } })
                .then(result => {
                    if (result && result.data) {
                        notify(translate('common.importSection.dataImportedSuccess'), {
                            type: 'success',
                            action: (
                                <Button color='primary' size='small' onClick={() => window.location.reload()}>
                                    {translate('common.reload')}
                                </Button>
                            ),
                        });
                        refresh();
                        onClose();
                        if (onImportSuccess) {
                            const failedCount = list.length - devices.length;
                            onImportSuccess({ imported: devices.length, failed: failedCount });
                        }
                    }
                })
                .catch(error => {
                    errorHandler(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            errorHandler(error);
            setLoading(false);
        }
    };

    const renderTableCell = (fieldName, value, isBoolean = false, getError, getTooltip) => {
        const error = getError(fieldName);

        const getErrorMessage = errorObj => {
            if (!errorObj) return '';

            const contextParams = {};
            if (Array.isArray(errorObj.context)) {
                errorObj.context.forEach(item => {
                    if (item.key && item.value !== undefined) {
                        contextParams[item.key] = item.value;
                    }
                });
            }

            return translate(errorObj.message, contextParams);
        };

        const errorMessage = error ? getErrorMessage(error) : '';

        const displayValue = isBoolean
            ? value !== null && value !== undefined
                ? value
                    ? translate('common.boolean.yes')
                    : translate('common.boolean.no')
                : ' '
            : value || ' ';
        const customTooltip = typeof getTooltip === 'function' ? getTooltip() : '';
        const tooltipTitle = customTooltip || errorMessage || '';

        return (
            <TableCell sx={{ backgroundColor: error ? Palette.yellow.yellow50 : 'transparent' }} className={`${error ? 'error-cell' : ''}`}>
                <Tooltip title={tooltipTitle} arrow classes={{ tooltip: 'tooltip-white tooltip-white--import-table' }}>
                    <Box className='table-cell-content'>
                        {error && <WarningIcon />}
                        {displayValue}
                    </Box>
                </Tooltip>
            </TableCell>
        );
    };

    const fullList = Array.isArray(data?.list) ? data.list : [];
    const { validCount, invalidCount } = (() => {
        let valid = 0;
        let invalid = 0;
        for (let i = 0; i < fullList.length; i++) {
            const item = fullList[i] || {};
            const sel = selectedAddresses[i];
            const errors = Array.isArray(item?.errorList) ? item.errorList : [];
            const effectiveErrors = sel ? errors.filter(e => e.field !== 'formattedAddress' && e.field !== 'placeId') : errors;
            if (effectiveErrors.length === 0) valid++;
            else invalid++;
        }
        return { validCount: valid, invalidCount: invalid };
    })();

    return (
        <Drawer
            open={open}
            onClose={onClose}
            anchor='right'
            PaperProps={{
                className: 'custom-dialog-right full-width custom-dialog-right--no-resizer',
            }}
        >
            <Box class='custom-dialog-right__inner'>
                <Box className='popup-wrapper popup-wrapper-top'>
                    <Box className='popup-box popup-box--header'>
                        <Typography variant='h4'>{translate('common.importSection.title')}</Typography>
                        <Box>
                            <CustomCloseButton onClose={onClose} />
                        </Box>
                    </Box>
                </Box>
                <Box className='popup-wrapper'>
                    <StyledBox className='popup-box validation-summary'>
                        <Typography variant='body2' className='validation-summary-title'>
                            {translate('common.importSection.summaryTitle')}
                        </Typography>
                        {data ? (
                            <>
                                <Box className='validation-summary-details'>
                                    <Typography variant='body2'>
                                        <ActiveIcon />{' '}
                                        <Typography variant='body1'>
                                            <strong>
                                                {validCount} {pluralizeWord(validCount, 'common.importSection.record', translate)}{' '}
                                            </strong>
                                            {translate('common.importSection.recordsReadSuccess')}
                                        </Typography>
                                    </Typography>
                                    <Typography variant='body2' sx={{ '& svg path[fill="#FFC800"]': { fill: Palette.red.red400 } }}>
                                        <WarningIcon />
                                        <Typography variant='body1'>
                                            <strong>
                                                {invalidCount} {pluralizeWord(invalidCount, 'common.importSection.record', translate)}
                                            </strong>{' '}
                                            {translate('common.importSection.recordsHaveErrors')} <br />
                                            {translate('common.importSection.canCorrectFile')}
                                        </Typography>
                                    </Typography>
                                    <Button
                                        variant='contained'
                                        color='secondary'
                                        size='small'
                                        sx={{ gap: '8px' }}
                                        onClick={() => {
                                            onClose();
                                        }}
                                    >
                                        <UploadIcon /> {translate('common.importSection.selectFileAgain')}
                                    </Button>
                                </Box>
                                {Array.isArray(data.list) && data.list.length > 0 && (
                                    <Box sx={{ overflowX: 'auto', margin: '38px -56px 6px 0' }}>
                                        <Table size='small' className='import-table'>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ border: 'none !important', backgroundColor: 'transparent !important' }}></TableCell>
                                                    <TableCell>serialNumber</TableCell>
                                                    <TableCell>groupName</TableCell>
                                                    <TableCell>collectionPointId</TableCell>
                                                    <TableCell>collectionPointName</TableCell>
                                                    <TableCell>collectionPointLabel</TableCell>
                                                    <TableCell>collectionPointType</TableCell>
                                                    <TableCell>operator</TableCell>
                                                    <TableCell>operatorPointId</TableCell>
                                                    <TableCell>couponSource</TableCell>
                                                    <TableCell>couponRvmLocationId</TableCell>
                                                    <TableCell>couponCompPin</TableCell>
                                                    <TableCell>latitude</TableCell>
                                                    <TableCell>longitude</TableCell>
                                                    <TableCell>address</TableCell>
                                                    <TableCell>contactName</TableCell>
                                                    <TableCell>contactEmail</TableCell>
                                                    <TableCell>contactPhoneNumber</TableCell>
                                                    <TableCell>monFrom</TableCell>
                                                    <TableCell>monTo</TableCell>
                                                    <TableCell>monClosed</TableCell>
                                                    <TableCell>tueFrom</TableCell>
                                                    <TableCell>tueTo</TableCell>
                                                    <TableCell>tueClosed</TableCell>
                                                    <TableCell>wedFrom</TableCell>
                                                    <TableCell>wedTo</TableCell>
                                                    <TableCell>wedClosed</TableCell>
                                                    <TableCell>thuFrom</TableCell>
                                                    <TableCell>thuTo</TableCell>
                                                    <TableCell>thuClosed</TableCell>
                                                    <TableCell>friFrom</TableCell>
                                                    <TableCell>friTo</TableCell>
                                                    <TableCell>friClosed</TableCell>
                                                    <TableCell>satFrom</TableCell>
                                                    <TableCell>satTo</TableCell>
                                                    <TableCell>satClosed</TableCell>
                                                    <TableCell>sunFrom</TableCell>
                                                    <TableCell>sunTo</TableCell>
                                                    <TableCell>sunClosed</TableCell>
                                                    <TableCell>contractorId</TableCell>
                                                    <TableCell>warrantyDate</TableCell>
                                                    <TableCell>collectionType</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {data.list.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, idx) => {
                                                    const globalIndex = page * rowsPerPage + idx;
                                                    const dev = item?.device ?? {};
                                                    const errors = Array.isArray(item?.errorList) ? item.errorList : [];
                                                    const hasSelectedAddress = Boolean(selectedAddresses[globalIndex]);
                                                    const rowErrors = hasSelectedAddress
                                                        ? errors.filter(e => e.field !== 'formattedAddress' && e.field !== 'placeId')
                                                        : errors;
                                                    const getError = field => rowErrors.find(e => e.field === field);
                                                    return (
                                                        <TableRow key={idx} hover>
                                                            <TableCell sx={{ border: 'none !important', padding: '12px 20px !important' }}>
                                                                {rowErrors.length === 0 ? <ActiveIcon /> : <WarningIcon />}
                                                            </TableCell>
                                                            {renderTableCell('serialNumber', dev.serialNumber, false, getError)}
                                                            {renderTableCell('groupName', dev.groupName, false, getError)}
                                                            {renderTableCell('collectionPointId', dev.collectionPointId, false, getError)}
                                                            {renderTableCell('collectionPointName', dev.collectionPointName, false, getError)}
                                                            {renderTableCell('collectionPointLabel', dev.collectionPointLabel, false, getError)}
                                                            {renderTableCell('collectionPointType', dev.collectionPointType, false, getError)}
                                                            {renderTableCell('operator', dev.operator, false, getError)}
                                                            {renderTableCell('operatorPointId', dev.operatorPointId, false, getError)}
                                                            {renderTableCell('couponSource', dev.couponSource, false, getError)}
                                                            {renderTableCell('couponRvmLocationId', dev.couponRvmLocationId, false, getError)}
                                                            {renderTableCell('couponCompPin', dev.couponCompPin, false, getError)}
                                                            {renderTableCell('latitude', dev.latitude, false, getError)}
                                                            {renderTableCell('longitude', dev.longitude, false, getError)}
                                                            {renderTableCell(
                                                                'formattedAddress',
                                                                selectedAddresses[globalIndex]?.formattedAddress || dev.formattedAddress,
                                                                false,
                                                                getError,
                                                                () => {
                                                                    if (selectedAddresses[globalIndex]) return undefined;

                                                                    const addressError = getError('address');
                                                                    const cityError = getError('city');
                                                                    const postalCodeError = getError('postalCode');
                                                                    const provinceError = getError('province');
                                                                    const countryError = getError('country');

                                                                    const hasAnyError =
                                                                        addressError || cityError || postalCodeError || provinceError || countryError;

                                                                    if (hasAnyError) {
                                                                        const errorMessages = [];
                                                                        errorMessages.push(
                                                                            `<strong>${translate('common.importSection.noSuggestedAddresses')} <br /><br />${translate('common.importSection.errorsInFields')} </strong><br />`,
                                                                        );
                                                                        if (addressError) errorMessages.push(`<strong>address:</strong> ${addressError}`);
                                                                        if (cityError) errorMessages.push(`<strong>city:</strong> ${cityError}`);
                                                                        if (postalCodeError)
                                                                            errorMessages.push(`<strong>postalCode:</strong> ${postalCodeError}`);
                                                                        if (provinceError) errorMessages.push(`<strong>province:</strong> ${provinceError}`);
                                                                        if (countryError) errorMessages.push(`<strong>country:</strong> ${countryError}`);

                                                                        const message = errorMessages.join('<br />');
                                                                        return <Box dangerouslySetInnerHTML={{ __html: message }} sx={{ fontWeight: '400' }} />;
                                                                    } else {
                                                                        const suggestions = Array.isArray(dev?.addressSuggestions)
                                                                            ? dev.addressSuggestions
                                                                            : [];
                                                                        if (suggestions.length === 0) return '';
                                                                        return (
                                                                            <Box className='address-suggestions'>
                                                                                <Typography variant='body2'>
                                                                                    {translate('common.importSection.suggestedResults')}
                                                                                </Typography>
                                                                                {suggestions.map((s, i) => (
                                                                                    <Box
                                                                                        className='address-option'
                                                                                        style={{ padding: '16px', cursor: 'pointer' }}
                                                                                        key={i}
                                                                                        onClick={() => {
                                                                                            setSelectedAddresses(prev => ({
                                                                                                ...prev,
                                                                                                [globalIndex]: {
                                                                                                    placeId: s.placeId,
                                                                                                    formattedAddress: s.formattedAddress,
                                                                                                },
                                                                                            }));
                                                                                        }}
                                                                                    >
                                                                                        {s.formattedAddress}
                                                                                    </Box>
                                                                                ))}
                                                                            </Box>
                                                                        );
                                                                    }
                                                                },
                                                            )}
                                                            {renderTableCell('contactName', dev.contactName, false, getError)}
                                                            {renderTableCell('contactEmail', dev.contactEmail, false, getError)}
                                                            {renderTableCell('contactPhoneNumber', dev.contactPhoneNumber, false, getError)}
                                                            {renderTableCell('monFrom', dev.monFrom, false, getError)}
                                                            {renderTableCell('monTo', dev.monTo, false, getError)}
                                                            {renderTableCell('monClosed', dev.monClosed, true, getError)}
                                                            {renderTableCell('tueFrom', dev.tueFrom, false, getError)}
                                                            {renderTableCell('tueTo', dev.tueTo, false, getError)}
                                                            {renderTableCell('tueClosed', dev.tueClosed, true, getError)}
                                                            {renderTableCell('wedFrom', dev.wedFrom, false, getError)}
                                                            {renderTableCell('wedTo', dev.wedTo, false, getError)}
                                                            {renderTableCell('wedClosed', dev.wedClosed, true, getError)}
                                                            {renderTableCell('thuFrom', dev.thuFrom, false, getError)}
                                                            {renderTableCell('thuTo', dev.thuTo, false, getError)}
                                                            {renderTableCell('thuClosed', dev.thuClosed, true, getError)}
                                                            {renderTableCell('friFrom', dev.friFrom, false, getError)}
                                                            {renderTableCell('friTo', dev.friTo, false, getError)}
                                                            {renderTableCell('friClosed', dev.friClosed, true, getError)}
                                                            {renderTableCell('satFrom', dev.satFrom, false, getError)}
                                                            {renderTableCell('satTo', dev.satTo, false, getError)}
                                                            {renderTableCell('satClosed', dev.satClosed, true, getError)}
                                                            {renderTableCell('sunFrom', dev.sunFrom, false, getError)}
                                                            {renderTableCell('sunTo', dev.sunTo, false, getError)}
                                                            {renderTableCell('sunClosed', dev.sunClosed, true, getError)}
                                                            {renderTableCell('contractorId', dev.contractorId, false, getError)}
                                                            {renderTableCell('warrantyDate', dev.warrantyDate, false, getError)}
                                                            {renderTableCell('collectionType', dev.collectionType, false, getError)}
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                        {data.list.length > 10 && (
                                            <TablePagination
                                                rowsPerPageOptions={[5, 10, 25, 50]}
                                                component='div'
                                                count={data.list.length}
                                                rowsPerPage={rowsPerPage}
                                                page={page}
                                                onPageChange={handleChangePage}
                                                onRowsPerPageChange={handleChangeRowsPerPage}
                                                labelRowsPerPage={translate('common.importSection.rowsPerPage')}
                                                labelDisplayedRows={({ from, to, count }) => `${from}-${to} z ${count}`}
                                            />
                                        )}
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Typography variant='body2'>{translate('common.importSection.noValidationData')}</Typography>
                        )}
                    </StyledBox>
                </Box>
                <Box className='popup-wrapper popup-wrapper--footer'>
                    <Button onClick={onClose} color='secondary' startIcon={<ArrowLeftIcon />}>
                        {translate('common.cancel')}
                    </Button>
                    <Button onClick={submitValidData} variant='contained' disabled={loading}>
                        {loading ? translate('common.importSection.sendingData') : translate('common.importSection.importData')}
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
};

ImportValidationDetailsDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    data: PropTypes.object,
    resource: PropTypes.string.isRequired,
    dataTransformer: PropTypes.string.isRequired,
    setLoading: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    onImportSuccess: PropTypes.func,
};

export default ImportValidationDetailsDrawer;
